import * as React from 'react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ArtikelAttachment, deleteArtikelAttachment } from 'api/artikelAttachment';
import { getApiUrl } from 'api/useApi';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { AddIcon, DeleteIcon, EditIcon } from '@fluentui/react-icons';
import { formStyles } from 'styles/formStyles';
import { EditArtikelAttachment } from './EditArtikelAttachment';

const styles = mergeStyleSets({
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
    },
    title: {
        fontWeight: 600,
        marginTop: 5,
    },
    list: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr max-content max-content',
        gridColumnGap: 10,
    },
    addAction: {
        cursor: 'pointer',
        marginTop: 7,
    },
});

interface ArtikelAttachmentListProps {
    artikelNr: string;
    artikelAttachmentList: ArtikelAttachment[];
    refetchArtikel: () => void;
    onAdd: () => void;
    disabled?: boolean;
}

export const ArtikelAttachmentList: React.FC<ArtikelAttachmentListProps> = ({
    artikelNr,
    artikelAttachmentList,
    refetchArtikel,
    onAdd,
    disabled,
}) => {
    const auth = useSelector(authSelector);

    const [editArtikelAttachmentData, setEditArtikelAttachmentData] = React.useState<ArtikelAttachment | null>(null);
    const [deleteArtikelAttachmentData, setDeleteArtikelAttachmentData] = React.useState<ArtikelAttachment | null>(
        null,
    );

    return (
        <>
            <div className={styles.header}>
                <div className={styles.title}>Dokumente</div>
                {disabled || <AddIcon className={styles.addAction} onClick={onAdd} />}
            </div>
            <div className={styles.list}>
                {artikelAttachmentList.map((artikelAttachment) => {
                    const fileUrl =
                        getApiUrl('artikelattachment') +
                        '?artikelNr=' +
                        artikelNr +
                        '&fileName=' +
                        encodeURIComponent(artikelAttachment.file);

                    return (
                        <>
                            <div>{artikelAttachment.text}</div>
                            <div>
                                <a href={fileUrl} rel="noreferrer" target="_blank">
                                    {artikelAttachment.file}
                                </a>
                                {artikelAttachment.sendWithAngebot ? '*' : ''}
                            </div>
                            {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                                <>
                                    <EditIcon
                                        className={formStyles.positionenAction}
                                        onClick={() => setEditArtikelAttachmentData(artikelAttachment)}
                                    />
                                    <DeleteIcon
                                        className={formStyles.positionenAction}
                                        onClick={() => setDeleteArtikelAttachmentData(artikelAttachment)}
                                    />
                                </>
                            )}
                        </>
                    );
                })}
            </div>
            {editArtikelAttachmentData && (
                <EditArtikelAttachment
                    artikelAttachment={editArtikelAttachmentData}
                    onClose={(refetch) => {
                        if (refetch) {
                            refetchArtikel();
                        }
                        setEditArtikelAttachmentData(null);
                    }}
                />
            )}
            {deleteArtikelAttachmentData && (
                <ConfirmMessage
                    title="Dokument löschen"
                    minWidth={500}
                    content={
                        <>
                            Soll das Dokument{' '}
                            {deleteArtikelAttachmentData.file + ' / ' + deleteArtikelAttachmentData.text} gelöscht
                            werden?
                        </>
                    }
                    onConfirm={() => {
                        if (deleteArtikelAttachmentData) {
                            deleteArtikelAttachment(deleteArtikelAttachmentData).then(() => {
                                refetchArtikel();
                                setDeleteArtikelAttachmentData(null);
                            });
                        }
                    }}
                    onCancel={() => setDeleteArtikelAttachmentData(null)}
                />
            )}
        </>
    );
};
